import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { Dialog, Input, Button } from '@/design-system';

export interface TextInputDialogProps {
  open: boolean;
  title: string;
  label: string;
  initialValue?: string;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (value: string) => Promise<void>;
}

/**
 * Shared dialog shape for create/rename flows (SPEC-003 T038/T046: create
 * funnel name, rename funnel/folder/tag) — one accessible implementation
 * instead of near-duplicate modals per entity.
 */
export function TextInputDialog({
  open,
  title,
  label,
  initialValue = '',
  submitLabel = 'Salvar',
  onClose,
  onSubmit,
}: TextInputDialogProps) {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(value.trim());
      onClose();
    } catch {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label={label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          required
          disabled={submitting}
        />
        {error ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting || value.trim().length === 0}>
            {submitting ? 'Salvando…' : submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
